// Latest-wins, per-key trailing-debounce coalescer (D-10).
//
// Serves syncPeriodCounters's four call sites (useTransactionHandlers.js:111,
// :162-163, :199-200, :264-265) -- each of which already builds its full
// patched transaction array fresh, immediately before calling. Coalescing is
// correct *because* syncPeriodCounters is a full recompute over the whole
// array: the newest call subsumes an in-flight superseded one, so dropping
// the superseded call (rather than merging it) is the correct behavior, not
// a shortcut. See RESEARCH.md Pattern 3 and Pitfall 5.
//
// This module is deliberately retry-free. D-05's retry lives in the
// pending-saves registry (usePendingSaves.js) and deliberately BYPASSES this
// coalescer when it retries -- re-entering the coalescer would let a retry
// attempt land in a coalescing window opened by an unrelated newer save and
// get dropped as "superseded," which is strictly worse than the failure it
// was recovering from. Layering a second retry mechanism in here would
// reintroduce exactly that failure mode.
export function coalesceByKey(fn, windowMs = 400) {
    const pending = new Map(); // key -> { timer, latestArgs, resolvers: [] }

    return function coalesced(key, ...args) {
        return new Promise((resolve, reject) => {
            let entry = pending.get(key);
            if (!entry) {
                entry = { timer: null, latestArgs: args, resolvers: [] };
                pending.set(key, entry);
            } else {
                clearTimeout(entry.timer);
                // Latest-wins (D-10): overwrite with the newest call's
                // eagerly-captured arguments. Never merge/union with the
                // previous call's args -- each call already carries the
                // complete, correct-as-of-that-call state, so the newest run
                // subsumes the dropped one rather than needing to be
                // combined with it (Pitfall 5).
                entry.latestArgs = args;
            }
            entry.resolvers.push({ resolve, reject });
            entry.timer = setTimeout(async () => {
                // Delete BEFORE running fn -- a later call for this key must
                // start a fresh window rather than joining this settling one.
                pending.delete(key);
                try {
                    const result = await fn(...entry.latestArgs);
                    entry.resolvers.forEach((r) => r.resolve(result));
                } catch (error) {
                    // Surfaced to every queued caller as-is -- no retry here.
                    entry.resolvers.forEach((r) => r.reject(error));
                }
            }, windowMs);
        });
    };
}
